import React, { FC, useMemo, useState } from 'react';
import { IService } from '../../../@core/interfaces/IService';
import { useReservationStore } from '../../../stores/Reservation.store';
import ButtonPrimary from '../../../shared/Button/ButtonPrimary';
import moment from 'moment';
import { IReservation } from '../../../@core/interfaces/IReservation';

interface Props {
  service: IService;
  reservations: IReservation[]
}

const ReservationWeekView: FC<Props> = ({ service, reservations }: Props) => {
  const { setIsChoosingReservation, setDates, dates } = useReservationStore();
  const [offset, setOffset] = useState(0);

  const days = useMemo(() => {
    const start = moment().startOf('day').add(offset, 'day');
    return Array.from({ length: 5 }, (_, i) => start.clone().add(i, 'day').valueOf());
  }, [offset]);

  const maxOffset = 11;
  const canPrev = offset > 0;
  const canNext = offset < maxOffset;

  const handleSelect = (dayMs: number, time: string) => {
    const [h, m] = time.split(':').map(Number);
    const slotMs = moment(dayMs).hours(h).minutes(m || 0).seconds(0).milliseconds(0).valueOf();

    const newSelection = (() => {
      const idx = dates.findIndex(d => d === slotMs);
      if (idx !== -1) {
        const next = [...dates];
        next.splice(idx, 1);
        return next;
      }
      return [...dates, slotMs];
    })();

    setDates(newSelection);
  };

  const isReserved = (dayMs: number, time: string) => {
    const [h, m] = time.split(':').map(Number);
    const slotMs = moment(dayMs).hours(h).minutes(m || 0).seconds(0).milliseconds(0).valueOf();
    return reservations?.some((r: any) => r?.date === slotMs);
  };

  const isPast = (dayMs: number, time: string) => {
    const [h, m] = time.split(':').map(Number);
    const slot = moment(dayMs).hours(h).minutes(m || 0).seconds(0).milliseconds(0);
    return slot.isBefore(moment());
  };

  const formatDotDate = (dMs: number) => {
    return moment(dMs).format('DD.MM.YYYY');
  };

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-neutral-900 overflow-auto w-dvw h-dvh">
      <div className="container mx-auto py-6 px-4">
        <div className="flex items-center justify-between mb-6">
          <button
            className={`px-3 py-2 rounded-lg mr-2 ${canPrev ? 'bg-neutral-200 dark:bg-neutral-700' : 'bg-neutral-100 dark:bg-neutral-800 opacity-50 cursor-not-allowed'}`}
            disabled={!canPrev}
            onClick={() => canPrev && setOffset((v) => Math.max(0, v - 1))}
          >
            ←
          </button>
          <div className={'flex flex-col items-center'}>
            <h2 className="text-2xl font-semibold">Hourly Calendar</h2>
            <div className="mt-4 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3.5 h-3.5 rounded-sm bg-primary-6000"></span>
                <span>Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3.5 h-3.5 rounded-sm bg-yellow-400"></span>
                <span>Booked</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-2 rounded-lg ${canNext ? 'bg-neutral-200 dark:bg-neutral-700' : 'bg-neutral-100 dark:bg-neutral-800 opacity-50 cursor-not-allowed'}`}
              disabled={!canNext}
              onClick={() => canNext && setOffset((v) => Math.min(maxOffset, v + 1))}
            >
              →
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-neutral-200 dark:bg-neutral-700"
              onClick={() => setIsChoosingReservation(false)}
            >Close
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {days.map((dayMs) => {
            const dow = moment(dayMs).day();
            const weekday = moment(dayMs).format('dddd');
            const openTimes: string[] = service.details?.location?.openHours?.[dow] || [];
            return (
              <div
                key={dayMs.toString()}
                className="border rounded-xl p-3 border-neutral-200 dark:border-neutral-700"
              >
                <div className="font-semibold mb-2">
                  <div>{weekday}</div>
                  <div className="text-neutral-500 text-sm">{formatDotDate(dayMs)}</div>
                </div>
                <div className="flex flex-col gap-2">
                  {openTimes.length === 0 && (
                    <div className="text-sm text-neutral-500">Closed</div>
                  )}
                  {openTimes.map((time) => {
                    const reserved = isReserved(dayMs, time);
                    const past = isPast(dayMs, time);

                    const [h, m] = time.split(':').map(Number);
                    const slotMs = moment(dayMs).hours(h).minutes(m || 0).seconds(0).milliseconds(0).valueOf();
                    const isSelected = dates.some(d => d === slotMs);

                    const color = past
                      ? 'bg-gray-400 cursor-not-allowed'
                      : reserved
                        ? 'bg-yellow-400 cursor-not-allowed'
                        : isSelected
                          ? 'bg-primary-6000 hover:bg-primary-700'
                          : 'bg-green-500 hover:opacity-90';

                    const textColor = past
                      ? 'text-white'
                      : reserved
                        ? 'text-black'
                        : 'text-white';

                    const borderCls = 'border-2 border-transparent';

                    return (
                      <button
                        key={time}
                        disabled={past || reserved}
                        onClick={() => !reserved && !past && handleSelect(dayMs, time)}
                        className={`w-full ${color} ${textColor} ${borderCls} rounded-md px-2 py-1 text-sm text-left`}
                      >
                        {time} o'clock
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <ButtonPrimary className={'w-full mt-5'} onClick={() => setIsChoosingReservation(false)}>Close</ButtonPrimary>
      </div>
    </div>
  );
};

export default ReservationWeekView;
